import { inject, NgModule } from '@angular/core';
import { NavigationError, Router, RouterModule, Routes } from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { isOwner } from 'src/app/common/menu-def.provider';
import { NewEndOfTrialGuard } from 'src/app/guards/end-of-trial/new-end-of-trial.guard';
import { BillingGuard } from 'src/app/pages/billing/billing.guard';
import { CheckoutSuccessGuard } from 'src/app/pages/new-subscribe-plans/checkout-success/checkout-success.guard';
import { GrowthBookResolver } from 'src/app/services/growthbook/resolver';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ReportType } from 'src/models';
import { EmptyComponent } from '../components/empty/empty.component';
import { ErrorComponent } from '../components/error/error.component';
import { LogoutComponent } from '../components/logout/component';
import { CancelAccountHubspotSyncGuard } from '../components/new-cancel-account/cancel-account-hubspot-sync.guard';
import { AuthGuard } from '../guards/auth/auth.guard';
import { BasicPricingGuard } from '../guards/basic-pricing/basic-pricing.guard';
import { CompanySelectedGuard } from '../guards/company-selected/company-selected.guard';
import { ConfirmGuard } from '../guards/confirm/confirm.guard';
import { EndOfTrialGuard } from '../guards/end-of-trial/end-of-trial.guard';
import { FirstLoginGenesysGuard } from '../guards/first-login/first-login-genesys.guard';
import { FirstLoginGuard } from '../guards/first-login/first-login.guard';
import { HomeGuard } from '../guards/home/home.guard';
import { LoadAllUsersGuard } from '../guards/load-all-users.guard';
import { MaintenanceGuard } from '../guards/maintenance/maintenance.guard';
import { MenuGuard } from '../guards/menu/menu.guard';
import { PaidProtectedGuard } from '../guards/paid-protected/paid-protected.guard';
import { PayrollGuard } from '../guards/payroll/payroll.guard';
import { reactRedirectGuard } from '../guards/react-redirect/react-redirect.guard';
import { SubscribePlansGuard } from '../guards/subscribe-plans/subscribe-plans.guard';
import { TokenLoginGuard } from '../guards/token-login/token-login.guard';
import { BillingResolver } from '../services/billing/billing.resolver';
import { executiveDashboardUpsellGuard } from '../guards/upsell-redirect/executive-dashboard-upsell.guard';
import { approvalsSetupRedirectGuard } from '../guards/upsell-redirect/approvals-setup-redirect.guard';
import { attendanceSetupRedirectGuard } from '../guards/upsell-redirect/attendance-setup-redirect.guard';
import { featureFlagGuard } from '../guards/feature-flag/feature-flag.guard';
import { mcpEnabledGuard } from '../guards/feature-flag/mcp-enabled.guard';
import { upsellRedirectGuard } from '../guards/upsell-redirect/upsell-redirect.guard';
import { complianceDoctorAccessGuard } from '../guards/compliance-doctor-access/compliance-doctor-access.guard';
import { growthbookMatcher } from '../services/growthbook/matcher';
import { AuthState } from '../store/auth/auth.state';

/**
 * Restricts the cancel-your-account route to owners and users with explicit
 * billing access, matching the access model of other billing entry points.
 */
const cancelAccountBillingAccessGuard = () => {
  const store = inject(Store);
  const router = inject(Router);
  const company = store.selectSnapshot(AuthState.company);
  if (isOwner(company) || company?.userSettings?.billingAccess) {
    return true;
  }
  return router.createUrlTree(['/error', 'insufficientPermissions']);
};

export const routes: Routes = [
  {
    path: 'new',
    redirectTo: '',
  },
  {
    path: 'hours-tracked-new',
    redirectTo: '/hours-tracked-v2',
  },
  {
    path: 'ai-adoption',
    redirectTo: '/ai-impact',
  },
  {
    path: 'error',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'error/:error',
    component: ErrorComponent,
  },
  {
    path: 'logout',
    component: LogoutComponent,
  },
  {
    path: 'maintenance-mode',
    loadChildren: () => import('../pages/maintenance/maintenance.module').then(m => m.MaintenanceModule),
  },
  {
    path: 'login',
    loadChildren: () => import('../modules/login.routing').then(m => m.LoginRoutingModule),
  },
  {
    path: 'join',
    loadChildren: () => import('../pages/join/join.module').then(m => m.JoinModule),
  },
  {
    path: 'activate',
    loadChildren: () => import('../pages/activate/activate.module').then(m => m.ActivateModule),
  },
  {
    path: 'onboarding',
    canActivate: [AuthGuard, CompanySelectedGuard],
    loadChildren: () => import('../pages/onboarding/onboarding.module').then(m => m.OnboardingModule),
    data: { title: 'onboarding.title' },
  },
  {
    path: 'subscribe-plans',
    redirectTo: '/billing',
  },
  // End of Trial cutovers (HRCT-453): the gates stay on the parent so they
  // finish before the child's reactRedirectGuard can assign
  // document.location.href — same-array canActivate guards run in parallel.
  {
    canActivate: [AuthGuard, FirstLoginGenesysGuard, NewEndOfTrialGuard],
    path: 'new-end-of-trial',
    children: [
      {
        canActivate: [reactRedirectGuard('new-end-of-trial')],
        path: '',
        loadChildren: () => import('../pages/new-billing/new-end-of-trial/new-end-of-trial.module').then(m => m.NewEndOfTrialModule),
      },
    ],
  },
  {
    canActivate: [AuthGuard, FirstLoginGenesysGuard, EndOfTrialGuard],
    path: 'end-of-trial',
    children: [
      {
        canActivate: [reactRedirectGuard('end-of-trial')],
        path: '',
        loadChildren: () => import('../pages/end-of-trial/end-of-trial.module').then(m => m.EndOfTrialModule),
      },
    ],
  },
  {
    path: 'eot-subscribe-plans',
    redirectTo: '/billing',
  },
  {
    path: 'eot-subscribe-success',
    redirectTo: '/billing',
  },
  {
    canActivate: [AuthGuard, SubscribePlansGuard, FirstLoginGenesysGuard],
    path: 'new-subscribe-plans',
    loadChildren: () => import('../pages/new-subscribe-plans/new-subscribe-plans.module').then(m => m.NewSubscribePlansModule),
    data: { title: 'subscription.title' },
  },
  {
    canActivate: [CheckoutSuccessGuard],
    path: 'checkout-success',
    loadChildren: () => import('../pages/new-subscribe-plans/checkout-success/checkout-success.module').then(m => m.CheckoutSuccessModule),
  },
  {
    canActivate: [AuthGuard],
    path: 'office-vs-remote-v1',
    loadChildren: () => import('../pages/office-vs-remote/module').then(m => m.OfficeVsRemoteReportModule),
    canMatch: [growthbookMatcher('office-vs-remote-report')],
  },
  {
    canActivate: [AuthGuard],
    path: 'cloudassist-station-time',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('experimental-cloudassist-station-time')],
    data: {
      qlikConfig: {
        appId: '55262e3c-6c30-4b39-bcce-b03acd3c04a7',
        sheetId: '91c9aab4-142d-4ad1-a914-bd9c8dc42d88',
        title: 'CloudAssist Station Time',
        eventName: 'CloudAssist Station Time',
        pageId: 'cloudassist-station-time',
        useFallbackFromAPI: true, // Try to get IDs from API first, fallback to hardcoded values
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'app-url-search',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('experimental-app-url-search')],
    data: {
      qlikConfig: {
        appId: 'd52ffd62-4d3c-4a87-951c-5c2267dd8622',
        sheetId: 'yMWpU',
        title: 'App URL Search',
        eventName: 'App URL Search',
        pageId: 'app-url-search',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'wren-kitchens-app-url-search',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('wren-kitchens-url-search-report')],
    data: {
      qlikConfig: {
        appId: '313d3a9b-f9e6-44b7-bd47-f880f2b00413',
        sheetId: 'KBxPZE',
        title: 'Wren Kitchens App URL Search',
        eventName: 'Wren Kitchens App URL Search',
        pageId: 'wren-kitchens-url-search',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: '24hours-team-audit-log',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('24hours-team-audit-log')],
    data: {
      qlikConfig: {
        appId: '1b4b9951-bf6f-4051-b1f0-55c0f43881f5',
        sheetId: 'SXFm',
        title: '24Hours Team Audit Log',
        eventName: '24Hours Team Audit Log',
        pageId: '24hours-team-audit-log',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'celcoin-audit-log',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('celcoin-audit-log')],
    data: {
      qlikConfig: {
        appId: 'e3dcccdb-9af6-4bd0-a185-c083d4b1308b',
        sheetId: 'SXFm',
        title: 'Celcoin Audit Log',
        eventName: 'Celcoin Audit Log',
        pageId: 'celcoin-audit-log',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'chinabank-remotedata-report',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('chinabank-remote-data-report')],
    data: {
      qlikConfig: {
        appId: '95cb8b24-1500-4160-aab0-0d2cc820bd62',
        sheetId: 'RJBbv',
        title: 'China Bank Remote Data Report',
        eventName: 'ChinaBank RemoteData Report',
        pageId: 'chinabank-remotedata-report',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'retention-data-report',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('ring-ring-marketing-retention-data-report')],
    data: {
      qlikConfig: {
        appId: 'aac944f1-227e-4aec-9497-3704f1bea96d',
        sheetId: 'WFcNkS',
        title: 'Retention Data Report',
        eventName: 'Retention Data',
        pageId: 'retention-data-report',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'clarke-telecom-retention-data-report',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('clarke-telecom-retention-data-report')],
    data: {
      qlikConfig: {
        appId: '7adc7b2d-4cbc-4d55-980d-cec312150e9d',
        sheetId: 'WFcNkS',
        title: 'Clarke Telecom Retention Data Report',
        eventName: 'Clarke Telecom Retention Data',
        pageId: 'clarke-telecom-retention-data-report',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'lavaautomation-pto-report',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('lava-automation-retention-data-report')],
    data: {
      qlikConfig: {
        appId: '84c171c1-164a-40d2-a846-d716b45f0aae',
        sheetId: 'VtMb',
        title: 'Lava Automation PTO Report',
        eventName: 'Lava Automation PTO',
        pageId: 'lavaautomation-pto-report',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    canActivate: [AuthGuard],
    path: 'cresol-granular-data-report',
    loadChildren: () => import('../pages/qlik-report/module').then(m => m.QlikReportModule),
    canMatch: [growthbookMatcher('cresol-granular-data-report')],
    data: {
      qlikConfig: {
        appId: 'd0e8492e-c2d7-4bc4-be5b-adf4a9c1ce0d',
        sheetId: 'mmBHkR',
        title: 'Cresol Granular Data Report',
        eventName: 'Cresol Granular Data',
        pageId: 'cresol-granular-data-report',
        useFallbackFromAPI: false, // Use hardcoded values directly
      },
    },
  },
  {
    path: 'confirm',
    component: EmptyComponent,
    canActivate: [ConfirmGuard],
  },
  {
    path: 'reset-password',
    loadChildren: () => import('../pages/reset-password/reset-password.module').then(m => m.ResetPasswordModule),
    data: { skipAnalytics: true },
  },
  {
    path: 'genesys-onboarding',
    loadChildren: () => import('../pages/genesys-onboarding/module').then(m => m.GenesysOnboardingModule),
  },
  {
    path: '',
    canActivate: [TokenLoginGuard],
    children: [{
      path: '',
      canActivate: [AuthGuard],
      children: [{
        path: '',
        canActivate: [CompanySelectedGuard],
        resolve: { billingData: BillingResolver },
        children: [
          {
            path: 'new-billing',
            loadChildren: () => import('../pages/new-billing/new-billing.module').then(m => m.NewBillingModule),
            data: { title: 'billing.title' },
            resolve: { growthbook: GrowthBookResolver },
            canMatch: [async () => {
              const service = inject(GrowthBookService);
              await firstValueFrom(service.initialized.pipe(filter(x => x)));
              const value = service.getFeatureValue('new-billing-enhancement');
              return value === false || value === undefined || value === null;
            }],
          },
          {
            path: 'new-billing',
            loadChildren: () => import('../pages/new-billing/v2/new-billing.module').then(m => m.NewBillingModule),
            data: { title: 'billing.title' },
            canMatch: [growthbookMatcher('new-billing-enhancement', true)],
          },
          {
            canActivate: [PaidProtectedGuard, cancelAccountBillingAccessGuard, CancelAccountHubspotSyncGuard],
            path: 'cancel-your-account',
            loadChildren: () => import('../components/new-cancel-account/new-cancel-account.module').then(m => m.NewCancelAccountModule),
            data: { title: 'billing.cancelAccount' },
          },
          {
            path: '',
            canActivateChild: [MenuGuard, FirstLoginGenesysGuard, MaintenanceGuard],
            children: [
              {
                path: '',
                pathMatch: 'full',
                component: EmptyComponent,
                canActivate: [HomeGuard],
              },
              // Dashboard cutover routes: the gating guards sit on a pathless
              // parent's canActivateChild so they finish before reactRedirectGuard
              // can assign document.location.href. Same pattern as settings /
              // hours-tracked — same-array canActivate runs in parallel and the
              // redirect side-effect would otherwise race gated users onto React.
              // These wrappers must stay AFTER the HomeGuard landing route above:
              // an empty-path parent ahead of it swallows `/`.
              {
                path: '',
                canActivateChild: [FirstLoginGuard, PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('dashboard-team')],
                    path: 'dashboard',
                    loadChildren: () => import('../pages/dashboard/dashboard-team/dashboard-team.module').then(m => m.DashboardTeamModule),
                    data: { title: 'dashboard.titleTeam' },
                  },
                ],
              },
              {
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('dashboard-individual')],
                    path: 'dashboard-individual',
                    loadChildren: () => import('../pages/dashboard/dashboard-individual/dashboard-individual.module').then(m => m.DashboardIndividualModule),
                    data: { title: 'dashboard.titleIndividual' },
                  },
                ],
              },
              {
                path: '',
                canActivateChild: [PaidProtectedGuard, executiveDashboardUpsellGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('dashboard-executive')],
                    path: 'dashboard-executive',
                    loadChildren: () => import('../pages/dashboard/dashboard-executive/dashboard-executive.module').then(m => m.DashboardExecutiveModule),
                    data: { title: 'dashboard.titleExecutive' },
                  },
                ],
              },
              {
                // Admin Pages
                path: '',
                children: [
                  {
                    canActivate: [reactRedirectGuard('manage-users')],
                    path: 'manage-users',
                    loadChildren: () => import('../pages/manage-users/manage-users.module').then(m => m.ManageUsersModule),
                    data: { title: 'manageUsers.title' },
                  },
                  // Projects & Tasks settings cutover: PaidProtectedGuard on the
                  // parent canActivateChild so it finishes before reactRedirectGuard
                  // can assign document.location.href (HRCT-331).
                  {
                    path: '',
                    canActivateChild: [PaidProtectedGuard],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('projects-and-tasks')],
                        path: 'projects-and-tasks',
                        loadChildren: () => import('../pages/manage-projects/manage-projects.module').then(m => m.ManageProjectsModule),
                        data: { title: 'manageProjects.title' },
                      },
                    ],
                  },
                  // Settings cutover routes: PaidProtectedGuard on the parent
                  // canActivateChild so it finishes before reactRedirectGuard can
                  // assign document.location.href. Same pattern as timeline —
                  // same-array canActivate runs in parallel and the redirect
                  // side-effect would otherwise race unpaid users onto React.
                  {
                    path: '',
                    canActivateChild: [PaidProtectedGuard],
                    children: [
                      {
                        // Its own flag, as React reads it (HRCT-458).
                        canActivate: [reactRedirectGuard('manage-user-groups')],
                        path: 'manage-user-groups',
                        loadChildren: () => import('../pages/manage-groups/manage-groups.module').then(m => m.ManageGroupsModule),
                        data: { title: 'manageGroups.title' },
                      },
                      {
                        canActivate: [reactRedirectGuard('notifications')],
                        path: 'notifications',
                        loadChildren: () => import('../pages/notifications/module').then(m => m.NotificationsModule),
                        data: { title: 'notifications.title' },
                      },
                      {
                        // Redirect before LoadAllUsersGuard so cutover navigations
                        // skip the LoadManagedUsers dispatch.
                        canActivate: [reactRedirectGuard('settings'), LoadAllUsersGuard],
                        path: 'company',
                        loadChildren: () => import('../pages/company-settings/company-settings.module').then(m => m.CompanySettingsModule),
                      },
                    ],
                  },
                  {
                    canActivate: [PaidProtectedGuard],
                    path: 'invite',
                    loadChildren: () => import('../pages/invite/invite.module').then(m => m.InviteModule),
                    data: { title: 'invite.title' },
                  },
                  // Add Managers cutover: PaidProtectedGuard on the parent
                  // canActivateChild so it finishes before reactRedirectGuard
                  // can assign document.location.href (HRCT-331, HRCT-452).
                  {
                    path: '',
                    canActivateChild: [PaidProtectedGuard],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('add-managers')],
                        path: 'add-managers',
                        loadChildren: () => import('../pages/add-managers/add-managers.module').then(m => m.AddManagersModule),
                        data: { title: 'addManagers.title' },
                      },
                    ],
                  },
                  {
                    // Older AI Signals page. No longer linked from nav; kept reachable by direct URL. [AA-138]
                    canActivate: [PaidProtectedGuard],
                    path: 'insights-legacy',
                    loadChildren: () => import('../pages/insights/module').then(m => m.ProductivityInsightsModule),
                    data: { title: 'insights.title' },
                  },
                  // Schedules cutover route: PaidProtectedGuard and the upsell
                  // gate on the parent canActivateChild, so both finish before
                  // reactRedirectGuard can assign document.location.href. Same
                  // pattern as the settings routes above; in one canActivate
                  // array the guards run in parallel and the redirect would race
                  // unpaid or un-entitled users onto React.
                  {
                    path: '',
                    canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('schedules')],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('schedules')],
                        path: 'schedules',
                        loadChildren: () => import('../pages/schedules/module').then(m => m.SchedulesModule),
                        data: { title: 'schedules.title' },
                      },
                    ],
                  },
                  {
                    canActivate: [EndOfTrialGuard, BillingGuard],
                    path: 'billing',
                    loadChildren: () => import('../pages/billing/billing.module').then(m => m.BillingModule),
                    data: { title: 'billing.title' },
                  },
                  {
                    // Productivity Ratings cutover: paywall + upsell gates on the
                    // parent canActivateChild so they finish before
                    // reactRedirectGuard can assign document.location.href. Guards
                    // in one canActivate array run concurrently (HRCT-331).
                    path: '',
                    canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('productivity-ratings')],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('productivity-ratings')],
                        path: 'productivity-ratings',
                        loadChildren: () => import('../pages/productivity-ratings/productivity-ratings.module')
                          .then(m => m.ProductivityRatingsModule),
                        data: { title: 'productivityRating.title' },
                      },
                    ],
                  },
                  {
                    // Breaks cutover: paywall, plan and upsell gates on the parent
                    // canActivateChild so they finish before reactRedirectGuard can
                    // assign document.location.href (HRCT-331).
                    path: '',
                    canActivateChild: [PaidProtectedGuard, BasicPricingGuard, upsellRedirectGuard('breaks')],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('breaks')],
                        path: 'breaks',
                        loadChildren: () => import('../pages/breaks/breaks.module').then(m => m.BreaksModule),
                        data: { title: 'breaks.title' },
                      },
                    ],
                  },
                  {
                    // New AI Signals page. Nav link and TD logo point here. [AA-138]
                    canActivate: [PaidProtectedGuard],
                    path: 'signals',
                    loadChildren: () => import('../pages/ask-ai/ask-ai.module').then(m => m.AskAiModule),
                    data: { title: 'askAi.title' },
                  },
                  {
                    // Approvals cutover: paywall, plan and setup-redirect gates on
                    // the parent canActivateChild so they finish before
                    // reactRedirectGuard can assign document.location.href (HRCT-331).
                    path: '',
                    canActivateChild: [
                      PaidProtectedGuard,
                      BasicPricingGuard,
                      approvalsSetupRedirectGuard,
                    ],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('approvals')],
                        path: 'approvals',
                        loadChildren: () => import('../pages/approvals/approvals.module').then(m => m.ApprovalsModule),
                        data: { title: 'approvals.title' },
                      },
                    ],
                  },
                  {
                    canActivate: [PaidProtectedGuard],
                    path: 'ai-impact',
                    loadChildren: () => import('../pages/ai-adoption/module').then(m => m.AIAdoptionModule),
                    data: { title: 'aiAdoption.title' },
                  },
                ],
              },
              {
                // Hours Tracked / Projects & Tasks cutover: PaidProtectedGuard on the
                // parent canActivateChild so it finishes before reactRedirectGuard can
                // assign document.location.href. Same pattern as settings / timeline —
                // same-array canActivate runs in parallel and the redirect
                // side-effect would otherwise race unpaid users onto React.
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('hours-tracked')],
                    path: 'hours-tracked',
                    loadChildren: () => import('../pages/hours-tracked-new/module').then(x => x.HoursTrackedModule),
                    data: { title: 'hoursTracked.title', reportId: 'hoursTracked' as ReportType },
                    canMatch: [growthbookMatcher('new-hours-tracked-report')],
                  },
                  {
                    canActivate: [reactRedirectGuard('projects-report')],
                    path: 'projects-report',
                    loadChildren: () => import('../pages/projects-report-new/module').then(x => x.ProjectsReportModule),
                    data: { title: 'projects.title', reportId: 'projectsAndTasks' as ReportType },
                    canMatch: [growthbookMatcher('new-projects-report')],
                  },
                ],
              },
              {
                canActivate: [PaidProtectedGuard],
                path: 'hours-tracked-v2',
                loadChildren: () => import('../pages/hours-tracked-new/module').then(x => x.HoursTrackedModule),
                data: { title: 'hoursTracked.title', reportId: 'hoursTracked' as ReportType },
              },
              {
                canActivate: [PaidProtectedGuard],
                path: 'projects-report-v2',
                loadChildren: () => import('../pages/projects-report-new/module').then(x => x.ProjectsReportModule),
                data: { title: 'projects.title', reportId: 'projectsAndTasks' as ReportType },
              },
              {
                // Reports
                path: '',
                canActivateChild: [PaidProtectedGuard, LoadAllUsersGuard],
                children: [
                  {
                    path: 'timeline',
                    canActivate: [reactRedirectGuard('timeline')],
                    loadChildren: () => import('../pages/timeline/module').then(m => m.TimelineReportModule),
                    data: { title: 'timeline.title', reportId: 'timeline' as ReportType },
                  },
                  {
                    // Activity Summary cutover: the paywall and upsell gates sit on a
                    // pathless parent's canActivateChild so they finish before
                    // reactRedirectGuard can assign document.location.href (see the
                    // dashboard block above). Without the redirect, a user arriving
                    // from another Angular page stayed on Angular while the same URL
                    // reached by in-app React navigation rendered React (HRCT-340).
                    path: '',
                    canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('activity-summary')],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('activity-summary')],
                        path: 'activity-summary',
                        loadChildren: () => import('../pages/activity-summary/activity-summary.module').then(m => m.ActivitySummaryModule),
                        data: { title: 'activitySummary.title', reportId: 'activitySummary' as ReportType },
                      },
                    ],
                  },
                  {
                    path: 'hours-tracked',
                    canActivate: [reactRedirectGuard('hours-tracked')],
                    loadChildren: () => import('../pages/hours-tracked/hours-tracked.module').then(m => m.HoursTrackedModule),
                    data: { title: 'hoursTracked.title', reportId: 'hoursTracked' as ReportType },
                  },
                  {
                    path: 'hours-tracked-v1',
                    loadChildren: () => import('../pages/hours-tracked/hours-tracked.module').then(m => m.HoursTrackedModule),
                    data: { title: 'hoursTracked.title', reportId: 'hoursTracked' as ReportType },
                  },
                  {
                    path: 'projects-report',
                    canActivate: [reactRedirectGuard('projects-report')],
                    loadChildren: () => import('../pages/projects-report/projects-report.module').then(m => m.ProjectsReportModule),
                    data: { title: 'projects.title', reportId: 'projectsAndTasks' as ReportType },
                  },
                  {
                    path: 'projects-report-v1',
                    loadChildren: () => import('../pages/projects-report/projects-report.module').then(m => m.ProjectsReportModule),
                    data: { title: 'projects.title', reportId: 'projectsAndTasks' as ReportType },
                  },
                  {
                    path: 'upgrade',
                    loadChildren: () => import('../pages/upgrade/upgrade.module').then(m => m.UpgradeModule),
                    data: { title: 'upgrade.title' },
                  },
                  {
                    path: 'setup',
                    loadChildren: () => import('../pages/setup/setup.module').then(m => m.SetupModule),
                    data: { title: 'approvals.title' },
                  },
                  {
                    // Benchmarks cutover: paywall + upsell gates on the parent
                    // canActivateChild so they finish before reactRedirectGuard can
                    // assign document.location.href (HRCT-331).
                    path: '',
                    canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('benchmarks')],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('benchmarks')],
                        path: 'benchmarks',
                        loadChildren: () => import('../pages/benchmarking/benchmarking.module').then(m => m.BenchmarkingModule),
                        data: { title: 'benchmarking.title', reportId: 'benchmarks' as ReportType },
                      },
                    ],
                  },
                  {
                    canActivate: [PaidProtectedGuard],
                    path: 'workforce-intelligence',
                    loadChildren: () => import('../pages/workforce-intelligence/module').then(m => m.WorkforceIntelligenceModule),
                    data: { title: 'workforceIntelligence.title', reportId: 'workforceIntelligence' as ReportType },
                  },
                  {
                    // Office vs Remote cutover: same shape as Activity Summary above
                    // (HRCT-340).
                    path: '',
                    canActivateChild: [upsellRedirectGuard('office-vs-remote')],
                    children: [
                      {
                        canActivate: [reactRedirectGuard('office-vs-remote')],
                        path: 'office-vs-remote',
                        loadChildren: () => import('../pages/office-vs-remote-v2/module').then(m => m.OfficeVsRemoteV2ReportModule),
                        data: { title: 'reports.officeVsRemote', reportId: 'officeVsRemote' as ReportType },
                      },
                    ],
                  },
                  {
                    // When react-cutover-compliance-doctor is ON, reactRedirectGuard fires and
                    // React's ComplianceDoctorAccessGate owns access control from that point.
                    // complianceDoctorAccessGuard is the effective gatekeeper only while the flag is OFF.
                    canActivate: [complianceDoctorAccessGuard, reactRedirectGuard('compliance-doctor')],
                    path: 'compliance-doctor',
                    loadChildren: () => import('../pages/compliance-doctor/module').then(m => m.ComplianceDoctorModule),
                    data: { title: 'reports.complianceDoctor' },
                  },
                ],
              },
              {
                // Unusual Activity cutover: PaidProtectedGuard and the upsell redirect on
                // the parent canActivateChild so they finish before reactRedirectGuard can
                // assign document.location.href. Same pattern as settings / hours-tracked —
                // same-array canActivate runs in parallel and the redirect side-effect
                // would otherwise race unpaid or un-upgraded users onto React.
                path: '',
                canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('unusual-activity')],
                children: [
                  {
                    canActivate: [reactRedirectGuard('unusual-activity')],
                    path: 'unusual-activity',
                    loadChildren: () => import('../pages/unusual-activity/module').then(m => m.UnusualActivityModule),
                    data: { title: 'unusualActivity.title', reportId: 'unusualActivity' as ReportType },
                  },
                ],
              },
              {
                // Screencasts cutover: PaidProtectedGuard on the parent
                // canActivateChild so it finishes before reactRedirectGuard can
                // assign document.location.href (see the dashboard block above).
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('screencasts')],
                    path: 'screencasts',
                    loadChildren: () => import('../pages/screencasts/screencasts.module').then(m => m.ScreencastsModule),
                    data: { title: 'screencasts.title', reportId: 'screencasts' as ReportType },
                  },
                ],
              },
              {
                // Web & App Usage cutover: PaidProtectedGuard and the upsell redirect on
                // the parent canActivateChild so they finish before reactRedirectGuard can
                // assign document.location.href. Same pattern as settings / hours-tracked —
                // same-array canActivate runs in parallel and the redirect side-effect
                // would otherwise race unpaid or un-upgraded users onto React.
                path: '',
                canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('web-app-usage')],
                children: [
                  {
                    canActivate: [reactRedirectGuard('web-app-usage')],
                    path: 'web-app-usage',
                    loadChildren: () => import('../pages/productivity/productivity.module').then(m => m.ProductivityReportModule),
                    data: { title: 'productivity.title', reportId: 'webAndAppUsage' as ReportType },
                  },
                ],
              },
              {
                // Software Cost Insights cutover: paywall + upsell gates on the
                // parent canActivateChild so they finish before reactRedirectGuard
                // can assign document.location.href (HRCT-331). LoadAllUsersGuard
                // lives on the module's child route so cutover navigations skip
                // the users load.
                path: '',
                canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('software-cost-insights')],
                children: [
                  {
                    canActivate: [reactRedirectGuard('software-cost-insights')],
                    path: 'software-cost-insights',
                    loadChildren: () => import('../pages/software-cost-insights/software-cost-insights.module').then(m => m.SoftwareCostInsightsModule),
                    data: { title: 'softwareCostInsights.title' },
                  },
                ],
              },
              {
                // Internet Connectivity cutover: paywall + upsell gates on the parent
                // canActivateChild so they finish before reactRedirectGuard can
                // assign document.location.href (see the dashboard block above).
                path: '',
                canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('internet-connectivity')],
                children: [
                  {
                    canActivate: [reactRedirectGuard('internet-report')],
                    path: 'internet-report',
                    loadChildren: () => import('../pages/internet-report/internet-report.module').then(m => m.InternetConnectivityModule),
                    data: { title: 'connectivity.title', reportId: 'connectivity' as ReportType },
                  },
                ],
              },
              {
                // Meeting Insights cutover: gates on the parent so they finish
                // before reactRedirectGuard (HRCT-340). canMatch stays on the child,
                // so with the CTA flag off nothing under this parent matches.
                path: '',
                canActivateChild: [PaidProtectedGuard, upsellRedirectGuard('meeting-insights')],
                children: [
                  {
                    canActivate: [reactRedirectGuard('meeting-insights')],
                    path: 'meeting-insights',
                    loadChildren: () => import('../pages/meeting-insights/meeting-insights.module').then(m => m.MeetingInsightsModule),
                    data: { title: 'meetingInsights.title' },
                    canMatch: [growthbookMatcher('meeting-insights-in-dashboard-cta')],
                  },
                ],
              },
              {
                // Experimental Reports cutover: PaidProtectedGuard on the parent
                // canActivateChild so it finishes before reactRedirectGuard can
                // assign document.location.href. Same pattern as hours-tracked /
                // settings — same-array canActivate runs in parallel and the
                // redirect side-effect would otherwise race unpaid users onto React.
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('experimental-reports')],
                    path: 'experimental-reports',
                    loadChildren: () => import('../pages/experimental-reports/module').then(m => m.ExperimentalReportsModule),
                    data: { title: 'experimental.title', reportId: 'experimental' },
                  },
                ],
              },
              {
                // Same route as web-app-usage but included for backward-compatibility.
                // Deliberately *not* behind `upsellRedirectGuard`, unchanged from
                // before the cutover — see `td-react` web-app-usage BACKLOG §6.
                // `reactRedirectGuard` uses the report's own flag, so the alias
                // follows the report across the cutover and lands on `/r/productivity`.
                // PaidProtectedGuard on the parent canActivateChild so it finishes
                // before reactRedirectGuard can assign document.location.href.
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('web-app-usage')],
                    path: 'productivity',
                    loadChildren: () => import('../pages/productivity/productivity.module').then(m => m.ProductivityReportModule),
                    data: { title: 'productivity.title', reportId: 'webAndAppUsage' as ReportType },
                  },
                ],
              },
              {
                // Custom Export cutover (HRCT-340).
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('csv')],
                    path: 'csv',
                    loadChildren: () => import('../pages/csv/csv.module').then(m => m.CsvModule),
                    data: { title: 'csv.title', reportId: 'csv' as ReportType },
                  },
                ],
              },
              {
                canActivate: [reactRedirectGuard('downloads')],
                path: 'downloads',
                loadChildren: () => import('../pages/downloads/downloads.module').then(m => m.DownloadsModule),
                data: { title: 'download.title' },
              },
              {
                // Integrations cutover: PaidProtectedGuard on the parent
                // canActivateChild so it finishes before reactRedirectGuard can
                // assign document.location.href (HRCT-331).
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('integrations')],
                    path: 'integrations',
                    loadChildren: () => import('../pages/integrations/integrations.module').then(m => m.IntegrationsModule),
                    data: { title: 'integrations.title' },
                  },
                ],
              },
              // AI Connections cutover route: same canActivateChild pattern as
              // Edit Time below, so the paid and MCP gates finish before the
              // React handoff.
              {
                path: '',
                canActivateChild: [PaidProtectedGuard, mcpEnabledGuard('/integrations')],
                children: [
                  {
                    canActivate: [reactRedirectGuard('ai-connections')],
                    path: 'ai-connections',
                    loadChildren: () => import('../pages/integrations/mcp-setup/mcp-setup.module').then(m => m.McpSetupModule),
                    data: { title: 'integrations.mcpSetup.pageTitle' },
                  },
                ],
              },
              // Edit Time cutover route: PaidProtectedGuard on the parent
              // canActivateChild so it finishes before reactRedirectGuard can
              // assign document.location.href. Same pattern as settings /
              // reports — same-array canActivate runs in parallel and the
              // redirect side-effect would otherwise race unpaid users onto
              // React. Safe here because it sits after the landing route
              // (`path: ''` + HomeGuard, above); an empty-path parent placed
              // before that one swallows `/` (app-web#4230).
              {
                path: '',
                canActivateChild: [PaidProtectedGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('edit-time')],
                    path: 'edit-time',
                    loadChildren: () => import('../pages/edit-time/edit-time.module').then(m => m.EditTimeModule),
                    data: { title: 'editTime.title' },
                  },
                ],
              },
              {
                // Payroll cutover: the gating guards sit on a pathless parent's
                // canActivateChild so they finish before reactRedirectGuard can
                // assign document.location.href. Same-array canActivate runs in
                // parallel, and the redirect would race gated users onto React.
                path: '',
                canActivateChild: [PaidProtectedGuard, BasicPricingGuard, PayrollGuard, upsellRedirectGuard('payroll')],
                children: [
                  {
                    canActivate: [reactRedirectGuard('payroll')],
                    path: 'payroll',
                    loadChildren: () => import('../pages/payroll/payroll.module').then(m => m.PayrollModule),
                    data: { title: 'payroll.title' },
                  },
                ],
              },
              {
                // Attendance cutover (HRCT-340).
                path: '',
                canActivateChild: [PaidProtectedGuard, attendanceSetupRedirectGuard],
                children: [
                  {
                    canActivate: [reactRedirectGuard('attendance')],
                    path: 'attendance',
                    loadChildren: () => import('../pages/attendance/module').then(m => m.AttendanceModule),
                    data: { title: 'attendance.title', reportId: 'attendance' as ReportType },
                  },
                ],
              },
            ],
          },
          {
            // My Account cutover (HRCT-152). PaidProtectedGuard sits on the
            // parent's canActivateChild so it finishes before reactRedirectGuard
            // can assign document.location.href (HRCT-331). The wrapper is an
            // empty-path parent, so it must stay after the MenuGuard block that
            // holds the HomeGuard landing route: placed before it, it swallows
            // `/` and login never leaves it (E2E run 35689544106).
            path: '',
            canActivateChild: [PaidProtectedGuard],
            children: [
              {
                canActivate: [reactRedirectGuard('profile-edit')],
                path: 'profile-edit',
                loadChildren: () => import('../pages/user-profile/user-profile.module').then(m => m.UserProfileModule),
                data: { title: 'userProfile.title' },
              },
            ],
          }],
      },
      ],
    }],
  },
  {
    path: '**',
    component: ErrorComponent,
    data: { error: 'pageNotFound' },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    onSameUrlNavigation: 'reload',
  })],
  exports: [RouterModule],
})
export class AppRoutingModule {
  constructor(private router: Router) {

    const prevErrorHandler = this.router.errorHandler;
    this.router.errorHandler = (err: any) => {
      if (err.name === 'ChunkLoadError') {
        return undefined;
      }

      return prevErrorHandler.call(this.router, err);
    };

    this.router.events.subscribe(err => {
      if (err instanceof NavigationError) {
        if (err.error.name === 'ChunkLoadError') {
          window.location.href = err.url;
        }
      }
    });
  }
}
